/**
 * Screenshots Configuration
 *
 * Maps features to their screenshots for the landing and features pages.
 * Add your screenshots to /public/prints/ folder following this naming convention.
 *
 * Naming convention:
 * - Single screenshot: feature-name.png (e.g., prontuario.png)
 * - Multiple screenshots: feature-name-1.png, feature-name-2.png, etc.
 *
 * Example folder structure:
 * /public/prints/
 *   ├── prontuario-1.png
 *   ├── prontuario-2.png
 *   ├── agenda-1.png
 *   ├── agenda-2.png
 *   ├── agenda-3.png
 *   ├── teleconsulta.png
 *   ├── ia-transcricao.png
 *   ├── ia-insights.png
 *   ├── dashboard-1.png
 *   ├── dashboard-2.png
 *   └── equipe.png
 */

import { landingColors } from '../theme/landingTheme';

// Base path for screenshots
const PRINTS_BASE_PATH = '/prints';

// Helper to generate image paths
const getImagePath = (filename: string) => `${PRINTS_BASE_PATH}/${filename}`;

// ============================================================================
// Feature Screenshots Configuration
// ============================================================================

export interface FeatureScreenshot {
  id: string;
  title: string;
  description?: string;
  images: string[];
  tag?: string;
  tagColor?: string;
}

/**
 * Feature screenshots for the Features page
 * Each feature can have one or multiple screenshots
 */
export const FEATURE_SCREENSHOTS: Record<string, FeatureScreenshot> = {
  prontuario: {
    id: 'prontuario',
    title: 'Prontuário Eletrônico',
    description: 'Registro completo de sessões e evolução clínica',
    images: [
      getImagePath('prontuario-1.png'),
    ],
    tag: 'Essencial',
    tagColor: landingColors.primary.main,
  },
  agenda: {
    id: 'agenda',
    title: 'Agenda Integrada',
    description: 'Visualização completa com lembretes automáticos',
    images: [
      getImagePath('agenda-1.png'),
      getImagePath('agenda-2.png'),
      getImagePath('agenda-3.png'),
    ],
    tag: 'Automação',
    tagColor: landingColors.primary.main,
  },
  teleconsulta: {
    id: 'teleconsulta',
    title: 'Teleconsulta HD',
    description: 'Videochamadas integradas ao prontuário',
    images: [
      getImagePath('teleconsulta-1.png'),
    ],
    tag: 'Vídeo HD',
    tagColor: landingColors.status.info,
  },
  ia: {
    id: 'ia',
    title: 'Inteligência Artificial',
    description: 'Transcrição automática e insights inteligentes',
    images: [
      getImagePath('ia-transcricao.png'),
      getImagePath('ia-insights.png'),
    ],
    tag: 'EXCLUSIVO',
    tagColor: '#9333EA',
  },
  dashboard: {
    id: 'dashboard',
    title: 'Dashboard Financeiro',
    description: 'Métricas e análises em tempo real',
    images: [
      getImagePath('dashboard-1.png'),
      getImagePath('dashboard-2.png'),
      getImagePath('dashboard-3.png'),
    ],
    tag: 'Analytics',
    tagColor: landingColors.accent.orange,
  },
  equipe: {
    id: 'equipe',
    title: 'Gestão de Equipe',
    description: 'Multi-profissionais com permissões personalizadas',
    images: [
      getImagePath('equipe-1.png'),
    ],
    tag: 'Escalável',
    tagColor: landingColors.status.warning,
  },
  documentos: {
    id: 'documentos',
    title: 'Documentos Clínicos',
    description: 'Recibos, declarações e relatórios automáticos',
    images: [
      getImagePath('documentos.png'),
    ],
    tag: 'Automático',
    tagColor: landingColors.accent.teal,
  },
  avaliacoes: {
    id: 'avaliacoes',
    title: 'Avaliações Clínicas',
    description: 'Instrumentos padronizados com pontuação automática',
    images: [
      getImagePath('avaliacoes.png'),
    ],
    tag: 'Estruturado',
    tagColor: landingColors.status.info,
  },
  'agendamento-portal': {
    id: 'agendamento-portal',
    title: 'Agendamento Online e Portal',
    description: 'Página pública e portal do paciente com magic link',
    images: [
      getImagePath('agendamento-online.png'),
      getImagePath('portal-paciente.png'),
    ],
    tag: 'NOVO',
    tagColor: '#9333EA',
  },
  'gestao-avancada': {
    id: 'gestao-avancada',
    title: 'Gestão Avançada',
    description: 'Fila de espera e dashboard de progresso',
    images: [
      getImagePath('fila-espera.png'),
      getImagePath('progresso-paciente.png'),
    ],
    tag: 'Produtividade',
    tagColor: landingColors.accent.orange,
  },
};

// ============================================================================
// Hero Slideshow Configuration
// ============================================================================

export interface HeroSlide {
  image: string;
  title: string;
  description?: string;
  tag?: string;
  tagColor?: string;
}

/**
 * Hero slideshow - 5 principais features
 * Reduzido para melhor experiência e foco
 */
export const HERO_SLIDES: HeroSlide[] = [
  {
    image: getImagePath('dashboard-1.png'),
    title: 'Dashboard Completo',
    description: 'Visualize todas as métricas da sua clínica em tempo real',
    tag: 'Visão Geral',
    tagColor: landingColors.primary.main,
  },
  {
    image: getImagePath('agenda-1.png'),
    title: 'Agenda Inteligente',
    description: 'Organize sua semana com lembretes automáticos',
    tag: 'Produtividade',
    tagColor: landingColors.accent.teal,
  },
  {
    image: getImagePath('agendamento-online.png'),
    title: 'Agendamento Online',
    description: 'Pacientes agendam direto pelo seu link personalizado',
    tag: 'NOVO',
    tagColor: '#9333EA',
  },
  {
    image: getImagePath('avaliacoes.png'),
    title: 'Avaliações Clínicas',
    description: 'Instrumentos padronizados com pontuação automática',
    tag: 'Estruturado',
    tagColor: landingColors.status.info,
  },
  {
    image: getImagePath('documentos.png'),
    title: 'Documentos Automáticos',
    description: 'Recibos, declarações e relatórios em segundos',
    tag: 'Automático',
    tagColor: landingColors.accent.teal,
  },
  {
    image: getImagePath('ia-insights.png'),
    title: 'Inteligência Artificial',
    description: 'Transcrição automática e insights inteligentes',
    tag: 'IA Exclusiva',
    tagColor: '#9333EA',
  },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get screenshots for a specific feature by ID
 */
export function getFeatureScreenshots(featureId: string): string[] {
  return FEATURE_SCREENSHOTS[featureId]?.images || [];
}

/**
 * Get all feature screenshots as a flat array for slideshow
 */
export function getAllScreenshots(): string[] {
  return Object.values(FEATURE_SCREENSHOTS).flatMap((feature) => feature.images);
}

/**
 * Check if screenshots exist for a feature
 * (Note: This is a client-side check, actual file existence is verified at runtime)
 */
export function hasScreenshots(featureId: string): boolean {
  return featureId in FEATURE_SCREENSHOTS && FEATURE_SCREENSHOTS[featureId].images.length > 0;
}

// Feature ID mapping for FeaturesPage integration
export const FEATURE_ID_MAP: Record<string, string> = {
  'Prontuário Eletrônico Completo': 'prontuario',
  'Prontuario Eletronico Completo': 'prontuario',
  'Agenda Integrada': 'agenda',
  'Teleconsulta Integrada': 'teleconsulta',
  'Inteligência Artificial': 'ia',
  'Inteligencia Artificial': 'ia',
  'Dashboard Financeiro': 'dashboard',
  'Multi-Profissionais': 'equipe',
  'Segurança e Conformidade': 'seguranca',
  'Seguranca e Conformidade': 'seguranca',
  // New features
  'Documentos Clínicos Automatizados': 'documentos',
  'Avaliações e Instrumentos Clínicos': 'avaliacoes',
  'Agendamento Online e Portal do Paciente': 'agendamento-portal',
  'Gestão Avançada de Agenda': 'gestao-avancada',
};

/**
 * Get feature ID from feature title
 */
export function getFeatureIdFromTitle(title: string): string | null {
  return FEATURE_ID_MAP[title] || null;
}
